import { useCallback, useEffect, useState } from "react";
import PageContainer from "../components/pageContainer/PageContainer";
import SettingsLayout from "../components/settings/SettingsLayout";
import Banner from "../components/banner/banner";
import Button from "../components/button";
import useModal from "../components/modal/useModal";
import UseContext from "../hooks/useContext";
import { Pencil, Plus, TrashIcon } from "../assets/icons";
import useTitle from "../hooks/TitleManager";
import useIntegrations from "../hooks/useIntegrations";
import ApiDatabase from "../server";
import { SettingsQualificationsListGhost } from "../components/ghost/SettingsAndEmployeesGhost";

const QualificationRow = ({ qualification, readOnly, onEdit, onDelete }) => (
  <div className="flex w-full items-center justify-between gap-4 rounded-lg border px-4 py-3">
    <div className="font-medium text-gray-900">{qualification.name}</div>
    {!readOnly && (
      <div className="flex gap-1">
        <Button color="ICON" size="ICON" onClick={() => onDelete(qualification)}>
          <TrashIcon wh={20} color="#374151" />
        </Button>
        <Button color="ICON" size="ICON" onClick={() => onEdit(qualification)}>
          <Pencil wh={20} color="#374151" />
        </Button>
      </div>
    )}
  </div>
);

const SettingsQualifications = () => {
  useTitle({ title: "Paramètres - Postes" });
  const integrations = useIntegrations();

  const [qualifications, setQualifications] = useState({
    workfully: [],
    peopulse: [],
    jobbiz: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const { toggle } = useModal();
  const { setModalData } = UseContext();

  const refresh = useCallback(() => {
    setIsLoading(true);
    ApiDatabase.getQualifications(
      {},
      (data) => {
        setQualifications(
          data?.grouped || { workfully: [], peopulse: [], jobbiz: [] }
        );
        setIsLoading(false);
      },
      () => setIsLoading(false)
    );
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleOpenCreate = () => {
    setModalData({
      onSuccess: () => {
        refresh();
      },
    });
    toggle("create_qualification_simple");
  };

  const handleOpenEdit = (qualification) => {
    setModalData({
      qualification,
      onSuccess: () => {
        refresh();
      },
      onDeleteRequest: (q) => {
        // Pass deletion request back to delete modal
        setTimeout(() => {
          setModalData({
            qualification: q,
            onSuccess: () => {
              refresh();
            },
          });
          toggle("delete_qualification");
        }, 0);
      },
    });
    toggle("edit_qualification");
  };

  const handleOpenDelete = (qualification) => {
    setModalData({
      qualification,
      onSuccess: () => {
        refresh();
      },
    });
    toggle("delete_qualification");
  };

  const integrationLabel = (source) =>
    source === "peopulse" ? "Peopulse" : source === "jobbiz" ? "Jobbiz" : "Workfully";

  return (
    <PageContainer type="settings_qualifications">
      <div className="flex w-full max-w-[980px] flex-col gap-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          <SettingsLayout>
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between pl-4">
                <h1 className="ft-xl font-semibold text-gray-900">Postes</h1>
                <Button
                  color="PRIMARY"
                  size="SM"
                  onClick={handleOpenCreate}
                >
                  <div className="flex items-center gap-1">
                    <Plus wh={20} color="#FFFFFF" />
                    Créer
                  </div>
                </Button>
              </div>

              {integrations.hasAny && (
                <Banner type="info" hideIcon>
                  <div className="rounded-lg p-3">
                    <div className="font-semibold text-gray-900">
                      Intégrations
                    </div>
                    <div className="text-gray-700 ft-sm">
                      Les postes sont automatiquement importés depuis vos
                      intégrations. Vous pouvez tout de même créer des postes
                      manuels.
                    </div>
                  </div>
                </Banner>
              )}

              <section className="flex flex-col gap-3">
                {isLoading ? (
                  <SettingsQualificationsListGhost />
                ) : (qualifications.workfully || []).length === 0 ? (
                  <div className="rounded-lg border p-4">
                    <div className="font-medium">Aucun poste créé</div>
                    <div className="ft-sm text-gray-500">
                      Vous n'avez pas encore créé de poste.
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto">
                    {qualifications.workfully.map((qualification) => (
                      <QualificationRow
                        key={qualification._id}
                        qualification={qualification}
                        onEdit={handleOpenEdit}
                        onDelete={handleOpenDelete}
                      />
                    ))}
                  </div>
                )}
              </section>

              {["peopulse", "jobbiz"].map((source) => {
                if (!integrations[source]) return null;
                const list = qualifications[source] || [];
                return (
                  <section key={source} className="flex flex-col gap-3">
                    <h2 className="text-base font-semibold text-gray-900 pl-4">
                      {integrationLabel(source)}
                    </h2>
                    {list.length === 0 ? (
                      <div className="ft-sm text-gray-500 pl-4">
                        Aucun poste synchronisé.
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto">
                        {list.map((qualification) => (
                          <QualificationRow
                            key={qualification._id}
                            qualification={qualification}
                            readOnly
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          </SettingsLayout>

          <aside className="hidden lg:block">
            <div className="sticky top-6 rounded-xl border bg-white p-5">
              <div className="font-semibold text-gray-900">À propos des postes</div>
              <p className="mt-2 ft-sm text-gray-600">
                Les postes vous permettent de catégoriser vos missions par
                fonction (chef de chantier, manutentionnaire, ...). Vous pouvez
                les créer un par un ou les importer en masse via un fichier
                CSV.
              </p>
              <p className="mt-3 ft-sm text-gray-600">
                Les postes Peopulse et Jobbiz sont synchronisés automatiquement
                depuis vos intégrations.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </PageContainer>
  );
};

export default SettingsQualifications;
